import Cookies from 'js-cookie';

import { PLATFORM_COOKIES } from '@/features/cookies/platform-cookies';
import { getUserOS } from '@/features/game/platform-helpers';
import {
  PokerProviderNamesToFgp,
  ProviderNameToFgp,
} from '@/features/game/types';
import {
  Channel,
  GenericProvider,
  Platform,
  Product,
  Provider,
} from '@/features/game-context/types';

export const isAndroidWrapper = (): boolean =>
  (window.MobileWrapper?.isWrapper?.() ?? false) ||
  (window?.AndroidWrapper?.isWrapper?.() ?? false);

export const isIosWrapper = (): boolean =>
  Cookies.get(PLATFORM_COOKIES.WRAPPER) === 'iOS' ||
  window?.MobileWrapper?.getWrapperType?.() === 'iOS';

// The v3 game-window (gw) shell owns the GeoComply flow, so when the client is
// embedded in it the client must not run its own geolocation handling. Detected
// via the launcher/shell referrer (mirrors resolve-launcher-message-origin).
export const isEmbeddedInGameWindowV3 = (
  referrer: string = document.referrer,
): boolean =>
  referrer.includes('https://gw') || referrer.includes('.gw.devstack');

export const getPlatform = (): Platform => {
  if (process.env.PRODUCT === Product.Xsell) {
    return Platform.Xsell;
  }

  if (isAndroidWrapper()) {
    return Platform.Android;
  }

  if (isIosWrapper()) {
    return Platform.iOS;
  }

  const userOS = getUserOS()?.toLowerCase();
  if (userOS === 'ios' || userOS === 'android') {
    return Platform.MobileWeb;
  }

  return Platform.Web;
};

export const getProduct = (): Product | undefined => {
  if (
    [Product.Casino, Product.Xsell, Product.Poker].includes(
      process.env.PRODUCT as Product,
    )
  ) {
    return process.env.PRODUCT as Product;
  }

  return undefined;
};

export const getProviderProduct = (
  provider?: Provider | GenericProvider,
): Product.Casino | Product.Poker =>
  provider && PokerProviderNamesToFgp.includes(ProviderNameToFgp[provider])
    ? Product.Poker
    : Product.Casino;

export const getChannel = (): Channel | undefined => {
  const product = getProduct();

  if (!product) return undefined;

  const channelMap: Record<Product, Channel> = {
    [Product.Casino]: Channel.Casino,
    [Product.Xsell]: Channel.Xsell,
    [Product.Poker]: Channel.Poker,
  };

  return channelMap[product];
};

export const getUserId = (): string | null =>
  Cookies.get(PLATFORM_COOKIES.USER_ID) || null;
